import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Typography,
  Alert,
} from '@mui/material';
import { axiosInstance } from '../../services/axiosInstance';
import { extractArrayFromApiPayload } from '../../utils/api';

export type MonitoringPoolAssignment = {
  id: number;
  code: string;
  name: string;
  monitoring_admin_id?: number | null;
  monitoring_admin_username?: string | null;
  monitoring_admin_name?: string | null;
};

type Props = {
  /** 현재 편집 중인 관리자 id (추가 시 null) */
  adminId: number | null;
  selectedPoolIds: number[];
  onChange: (ids: number[]) => void;
  /** 시설 전체 관제 (타 관리자 배당분 포함) */
  monitorAllPools?: boolean;
  onMonitorAllPoolsChange?: (value: boolean) => void;
  disabled?: boolean;
  /** 최고관리자이면 숨김 */
  hidden?: boolean;
};

function normalizeAssignmentRows(raw: unknown): MonitoringPoolAssignment[] {
  const list = Array.isArray(raw)
    ? raw
    : extractArrayFromApiPayload(raw);
  if (!Array.isArray(list)) return [];
  return list
    .map((p: any) => {
      const id = Number(p?.id);
      if (!Number.isFinite(id) || id <= 0) return null;
      return {
        id,
        code: String(p?.code ?? ''),
        name: String(p?.name ?? ''),
        monitoring_admin_id:
          p?.monitoring_admin_id != null && p.monitoring_admin_id !== ''
            ? Number(p.monitoring_admin_id)
            : null,
        monitoring_admin_username: p?.monitoring_admin_username ?? null,
        monitoring_admin_name: p?.monitoring_admin_name ?? null,
      } as MonitoringPoolAssignment;
    })
    .filter(Boolean) as MonitoringPoolAssignment[];
}

/** 관제 시설 다중 체크. 다른 관리자 배당분은 비활성. 「시설 전체」는 예외. */
const MonitoringPoolAssignField: React.FC<Props> = ({
  adminId,
  selectedPoolIds,
  onChange,
  monitorAllPools = false,
  onMonitorAllPoolsChange,
  disabled,
  hidden,
}) => {
  const [rows, setRows] = useState<MonitoringPoolAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      try {
        const res = await axiosInstance.get('/api/users/monitoring-assignments');
        const assigned = normalizeAssignmentRows(res.data);
        if (assigned.length > 0) {
          setRows(assigned);
          return;
        }
      } catch {
        /* fall through */
      }

      const poolsRes = await axiosInstance.get('/api/pools/');
      const fromPools = normalizeAssignmentRows(poolsRes.data);
      setRows(fromPools);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : '관제 시설 목록을 불러오기 실패. 로그인 상태를 확인.'
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hidden) void load();
  }, [hidden, load]);

  const selectedSet = useMemo(() => new Set(selectedPoolIds.map(Number)), [selectedPoolIds]);

  const toggle = (poolId: number, checked: boolean) => {
    if (monitorAllPools) return;
    if (checked) {
      onChange(Array.from(new Set([...selectedPoolIds.map(Number), poolId])));
    } else {
      onChange(selectedPoolIds.map(Number).filter((id) => id !== poolId));
    }
  };

  const setAllPools = (checked: boolean) => {
    onMonitorAllPoolsChange?.(checked);
    if (checked) {
      onChange([]);
    }
  };

  if (hidden) return null;

  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      <Typography variant="subtitle2" gutterBottom>
        관제대상
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        개별 시설을 배당하거나 「시설 전체」를 선택. 시설 전체는 이미 다른 관리자에게 배당된
        시설도 관제 화면에서 볼 수 있음(배당을 뺏지 않음).
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={monitorAllPools}
            disabled={Boolean(disabled)}
            onChange={(e) => setAllPools(e.target.checked)}
          />
        }
        label="시설 전체"
        sx={{ mb: 0.5 }}
      />
      {loading ? (
        <CircularProgress size={22} />
      ) : error ? (
        <Alert severity="warning">{error}</Alert>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          등록된 시설 없음
        </Typography>
      ) : (
        <FormGroup
          sx={{
            maxHeight: 220,
            overflow: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            px: 1,
            opacity: monitorAllPools ? 0.55 : 1,
          }}
        >
          {rows.map((row) => {
            const assignedToOther =
              row.monitoring_admin_id != null &&
              (adminId == null || Number(row.monitoring_admin_id) !== Number(adminId));
            const checked = monitorAllPools || selectedSet.has(Number(row.id));
            const labelExtra = assignedToOther
              ? ` (배당: ${
                  row.monitoring_admin_name ||
                  row.monitoring_admin_username ||
                  row.monitoring_admin_id
                })`
              : '';
            return (
              <FormControlLabel
                key={row.id}
                control={
                  <Checkbox
                    size="small"
                    checked={checked}
                    disabled={Boolean(disabled || monitorAllPools || assignedToOther)}
                    onChange={(e) => toggle(Number(row.id), e.target.checked)}
                  />
                }
                label={`${row.code} — ${row.name}${labelExtra}`}
              />
            );
          })}
        </FormGroup>
      )}
    </Box>
  );
};

export default MonitoringPoolAssignField;
